import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SearchBar = ({ onClose }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const searchProducts = async () => {
      if (query.length < 2) {
        setResults([]);
        return;
      }

      setLoading(true);
      try {
        const response = await axios.get(`${API}/products?search=${encodeURIComponent(query)}`);
        setResults(response.data.slice(0, 6));
      } catch (error) {
        console.error("Search error:", error);
      } finally {
        setLoading(false);
      }
    };

    const debounce = setTimeout(searchProducts, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleSelect = (productId) => {
    navigate(`/product/${productId}`);
    onClose?.();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query)}`);
      onClose?.();
    }
  };

  return (
    <div className="relative">
      <form onSubmit={handleSubmit} className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products..."
          data-testid="search-input"
          className="w-full pl-12 pr-12 py-3 bg-white border border-stone-200 rounded-full font-sans text-sm focus:outline-none focus:border-deep-moss"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </form>

      {/* Results Dropdown */}
      {query.length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-stone-200 rounded-lg shadow-lg z-50 overflow-hidden">
          {loading ? (
            <div className="p-4 text-center text-stone-500 font-sans text-sm">
              Searching...
            </div>
          ) : results.length > 0 ? (
            <div>
              {results.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleSelect(product.id)}
                  data-testid={`search-result-${product.id}`}
                  className="w-full flex items-center gap-4 p-3 hover:bg-stone-50 transition-colors text-left"
                >
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-12 h-12 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-sans text-sm font-medium text-stone-900 truncate">
                      {product.name}
                    </p>
                    <p className="font-sans text-xs text-stone-500 capitalize">
                      {product.category.replace("-", " ")}
                    </p>
                  </div>
                  <p className="font-sans text-sm font-semibold text-deep-moss">
                    ${product.price.toFixed(2)}
                  </p>
                </button>
              ))}
              <button
                onClick={handleSubmit}
                className="w-full p-3 text-center text-deep-moss font-sans text-sm hover:bg-stone-50 border-t border-stone-100"
              >
                View all results for &ldquo;{query}&rdquo;
              </button>
            </div>
          ) : (
            <div className="p-4 text-center text-stone-500 font-sans text-sm">
              No products found for &ldquo;{query}&rdquo;
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const SearchModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 bg-black/50"
        onClick={onClose}
      />
      <div className="fixed top-20 left-4 right-4 md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-xl z-50">
        <SearchBar onClose={onClose} />
      </div>
    </>
  );
};
